<template>
	<a-spin :spinning="spinning">
		<div class="report-container">
			<!-- Header & Filters -->
			<div class="row align-items-end mb-4">
				<div class="col-md-6 mb-3 mb-md-0">
					<h2 class="report-title">Depósitos de Efectivo</h2>
					<p class="report-subtitle">Reporte semanal por sucursal</p>
				</div>
				<div class="col-md-4 mb-3 mb-md-0">
					<span class="filter-label">Rango de fechas</span>
					<a-range-picker style="width: 100%" v-model="dates" @change="fetchData" size="large" />
				</div>
				<div class="col-md-2 text-right">
					<a-button type="primary" icon="reload" @click="fetchData" block size="large">Actualizar</a-button>
				</div>
			</div>

			<!-- Summary Cards -->
			<div class="row mb-4">
				<div class="col-6 col-lg-3 mb-3">
					<div class="summary-card neza-pacific">
						<div class="summary-icon"><a-icon type="shop" /></div>
						<div class="summary-content">
							<span class="summary-label">Neza S2R</span>
							<h3 class="summary-value">{{ numeral(summary.neza_pacific).format('$0,0') }}</h3>
						</div>
					</div>
				</div>
				<div class="col-6 col-lg-3 mb-3">
					<div class="summary-card neza-rls">
						<div class="summary-icon"><a-icon type="shop" /></div>
						<div class="summary-content">
							<span class="summary-label">Neza RLS</span>
							<h3 class="summary-value">{{ numeral(summary.neza_rls).format('$0,0') }}</h3>
						</div>
					</div>
				</div>
				<div class="col-6 col-lg-3 mb-3">
					<div class="summary-card chalco-pacific">
						<div class="summary-icon"><a-icon type="home" /></div>
						<div class="summary-content">
							<span class="summary-label">Chalco S2R</span>
							<h3 class="summary-value">{{ numeral(summary.chalco_pacific).format('$0,0') }}</h3>
						</div>
					</div>
				</div>
				<div class="col-6 col-lg-3 mb-3">
					<div class="summary-card chalco-rls">
						<div class="summary-icon"><a-icon type="home" /></div>
						<div class="summary-content">
							<span class="summary-label">Chalco RLS</span>
							<h3 class="summary-value">{{ numeral(summary.chalco_rls).format('$0,0') }}</h3>
						</div>
					</div>
				</div>
			</div>

			<!-- Details Table -->
			<div class="card border-0 shadow-sm overflow-hidden">
				<div class="table-responsive">
					<a-table :dataSource="reportData" :columns="columns" :pagination="false" bordered size="middle">
						<template slot="amount" slot-scope="text">
							<span class="amount-text">{{ numeral(text).format('$0,0.00') }}</span>
						</template>
						<template slot="weekLabel" slot-scope="text">
							<span class="week-text">{{ text }}</span>
						</template>
					</a-table>
				</div>
			</div>
		</div>
	</a-spin>
</template>

<script>
import moment from 'moment-timezone'
import 'moment/locale/es'
import numeral from 'numeral'
import _ from 'lodash'

export default {
	name: 'weeklyDepositsReportComponent',
	data() {
		return {
			spinning: false,
			dates: [moment().startOf('month'), moment().endOf('month')],
			reportData: [],
			numeral,
			columns: [
				{ title: 'Periodo de operación', dataIndex: 'weekLabel', key: 'weekLabel', align: 'left', scopedSlots: { customRender: 'weekLabel' } },
				{
					title: 'Neza',
					children: [
						{ title: 'S2R', dataIndex: 'branch1_pacific', key: 'branch1_pacific', scopedSlots: { customRender: 'amount' }, align: 'right' },
						{ title: 'RLS', dataIndex: 'branch1_rls', key: 'branch1_rls', scopedSlots: { customRender: 'amount' }, align: 'right' },
					],
				},
				{
					title: 'Chalco',
					children: [
						{ title: 'S2R', dataIndex: 'branch3_pacific', key: 'branch3_pacific', scopedSlots: { customRender: 'amount' }, align: 'right' },
						{ title: 'RLS', dataIndex: 'branch3_rls', key: 'branch3_rls', scopedSlots: { customRender: 'amount' }, align: 'right' },
					],
				},
			],
		}
	},
	computed: {
		summary() {
			return {
				neza_pacific: _.sumBy(this.reportData, 'branch1_pacific'),
				neza_rls: _.sumBy(this.reportData, 'branch1_rls'),
				chalco_pacific: _.sumBy(this.reportData, 'branch3_pacific'),
				chalco_rls: _.sumBy(this.reportData, 'branch3_rls'),
			}
		},
	},
	mounted() {
		moment.locale('es')
		this.fetchData()
	},
	methods: {
		formatPeriodLabel(start, end) {
			moment.locale('es')
			const startDay = start.format('DD')
			const endDay = end.format('DD')
			const monthName = end.format('MMMM')

			if (start.isSame(end, 'day')) {
				return `${startDay} de ${monthName}`
			}

			if (start.month() === end.month()) {
				return `${startDay} al ${endDay} de ${monthName}`
			} else {
				const startMonth = start.format('MMMM')
				return `${startDay} de ${startMonth} al ${endDay} de ${monthName}`
			}
		},
		async fetchData() {
			this.spinning = true
			moment.locale('es')
			const startDateStr = this.dates[0].format('YYYY-MM-DD')
			const endDateStr = this.dates[1].format('YYYY-MM-DD')

			try {
				// Ajustamos la consulta para traer un día más al final para cubrir el corte de las 10:00 AM
				const response = await this.$store.dispatch('pos/GET_TICKETS', {
					startDate: startDateStr,
					endDate: moment(endDateStr).add(1, 'day').format('YYYY-MM-DD'),
				})
				const tickets = response.tickets || []

				// Filtramos por sucursales 1, 3 y 4 y tickets activos
				// Aplicamos la lógica financiera: restar 10 horas a todos los tickets
				const filteredTickets = tickets
					.filter((t) => [1, 3, 4].includes(t.branch_office_id) && t.active > 0)
					.map((t) => {
						return {
							...t,
							financialDate: moment(t.created_at).tz('America/Mexico_City').subtract(10, 'hours'),
						}
					})

				// Generar periodos de operación (Lunes a Domingo / Corte de Mes)
				let periods = []
				let iter = moment.tz(startDateStr, 'America/Mexico_City')
				let currentStart = moment(iter)
				const endLimit = moment.tz(endDateStr, 'America/Mexico_City')

				while (iter.isSameOrBefore(endLimit, 'day')) {
					const isLastDayOfMonth = iter.isSame(moment(iter).endOf('month'), 'day')
					const isSunday = iter.day() === 0
					const isEndLimit = iter.isSame(endLimit, 'day')

					if (isLastDayOfMonth || isSunday || isEndLimit) {
						periods.push({
							start: currentStart.clone(),
							end: iter.clone(),
						})
						currentStart = iter.clone().add(1, 'day')
					}
					iter.add(1, 'day')
				}

				const report = periods.reverse().map((period) => {
					// Filtramos tickets usando su fecha financiera (con corte a las 10am)
					const periodTickets = filteredTickets.filter((t) => {
						return t.financialDate.isSameOrAfter(period.start, 'day') && t.financialDate.isSameOrBefore(period.end, 'day')
					})

					const getCashTotal = (tickets, branchIds, flag) => {
						if (!Array.isArray(branchIds)) branchIds = [branchIds]
						return _.sumBy(
							tickets.filter((t) => branchIds.includes(t.branch_office_id) && t[flag]),
							(t) => numeral(t.payment_cash).value(),
						)
					}

					return {
						key: period.start.format('YYYY-MM-DD'),
						weekLabel: this.formatPeriodLabel(period.start, period.end),
						branch1_pacific: getCashTotal(periodTickets, [1, 4], 'from_pacific'),
						branch1_rls: getCashTotal(periodTickets, [1, 4], 'from_rls'),
						branch3_pacific: getCashTotal(periodTickets, 3, 'from_pacific'),
						branch3_rls: getCashTotal(periodTickets, 3, 'from_rls'),
					}
				})

				// Solo mostramos periodos con actividad financiera
				this.reportData = report.filter((item) => item.branch1_pacific + item.branch1_rls + item.branch3_pacific + item.branch3_rls > 0)
			} catch (error) {
				console.error(error)
			} finally {
				this.spinning = false
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

.report-container {
	font-family: 'Inter', sans-serif;
	padding: 20px 0;
}

.report-title {
	font-weight: 700;
	color: #0f172a;
	margin-bottom: 4px;
	font-size: 24px;
}

.report-subtitle {
	color: #64748b;
	margin-bottom: 0;
	font-size: 14px;
}

.filter-label {
	display: block;
	font-size: 12px;
	font-weight: 600;
	text-transform: uppercase;
	color: #94a3b8;
	margin-bottom: 6px;
}

.summary-card {
	background: #ffffff;
	border-radius: 12px;
	padding: 16px;
	display: flex;
	align-items: center;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	transition: all 0.3s ease;
	height: 100%;
	border: 1px solid #f1f5f9;

	&:hover {
		transform: translateY(-2px);
		box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
	}

	.summary-icon {
		width: 44px;
		height: 44px;
		border-radius: 10px;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 20px;
		margin-right: 12px;
	}

	&.neza-pacific .summary-icon {
		background: #eff6ff;
		color: #2563eb;
	}
	&.neza-rls .summary-icon {
		background: #fef2f2;
		color: #dc2626;
	}
	&.chalco-pacific .summary-icon {
		background: #f0fdf4;
		color: #16a34a;
	}
	&.chalco-rls .summary-icon {
		background: #fffbeb;
		color: #d97706;
	}

	.summary-label {
		font-size: 12px;
		font-weight: 500;
		color: #64748b;
		display: block;
	}

	.summary-value {
		margin: 0;
		font-weight: 700;
		color: #1e293b;
		font-size: 18px;
	}
}

.amount-text {
	font-family: 'IBM Plex Mono', monospace;
	font-weight: 600;
	color: #334155;
}

.week-text {
	font-weight: 600;
	color: #475569;
}

@media (max-width: 768px) {
	.report-title {
		font-size: 20px;
	}
	.summary-card {
		padding: 12px;
		.summary-icon {
			width: 36px;
			height: 36px;
			font-size: 16px;
			margin-right: 8px;
		}
		.summary-value {
			font-size: 15px;
		}
	}
}
</style>



